import { CatalogBaseInfo, IContentAPICatalogBaseInfo } from "./CatalogBaseInfo";
import { CatalogClassification, IContentAPICatalogClassification } from "./CatalogClassification";
import { CatalogProperties, IContentAPICatalogProperties } from "./CatalogProperties";
import { CatalogVersion, IContentAPICatalogVersion } from "./CatalogVersion";
import { ImageRef, IContentAPIImageRef } from "./ImageRef";
import { PricingTaskList, IContentAPIPricingTaskList } from "./PricingTaskList";

export class CatalogFactory {
    static create(catalog: ICatalog | V2.ICatalog, catalogVersions?: Array<ICatalogVersion>): IContentAPICatalog {
        return new Catalog(catalog, catalogVersions).serialize();
    }
}

export class Catalog extends CatalogBaseInfo {
    protected _status: CATALOG_STATUS;
    protected _companyRefCodes?: Array<string>;
    protected _localization: ILocalization;
    protected _accessLevel?: string;
    protected _createdDate: string;
    protected _updatedDate: string;
    protected _altImages?: Array<ImageRef>;
    protected _classification?: CatalogClassification;
    protected _externalCodes?: IExternalCodes;
    protected _accessStatus?: string;
    // V2 Only
    protected _pricing?: PricingTaskList;
    protected _properties?: CatalogProperties;
    protected _catalogVersions?: Array<CatalogVersion>;

    constructor(catalog: ICatalog | V2.ICatalog, catalogVersions?: Array<ICatalogVersion>) {
        super(catalog);
        this._status = catalog.status;
        this._companyRefCodes = catalog.companyRefCodes;
        this._localization = catalog.localization;
        this._accessLevel = catalog.accessLevel;
        this._createdDate = catalog.createdDate;
        this._updatedDate = catalog.updatedDate;
        this._altImages = catalog.altImages?.map((imageRef: IImageRef) => new ImageRef(imageRef));
        this._classification = catalog.classification ? new CatalogClassification(catalog.classification) : undefined;
        this._externalCodes = catalog.externalCodes;
        this._accessStatus = catalog.accessStatus;
        this._catalogVersions = catalogVersions?.map((catalogVersionRef: ICatalogVersion) => new CatalogVersion(catalogVersionRef));

        // V2 Only
        let v2Catalog: V2.ICatalog = catalog as V2.ICatalog;
        if (v2Catalog.pricing) {
            this._pricing = new PricingTaskList(v2Catalog.pricing);
        }

        if (v2Catalog.properties) {
            this._properties = new CatalogProperties(v2Catalog.properties);
        }
    }

    serialize(): IContentAPICatalog {
        const catalog: IContentAPICatalog = {
            ...super.serialize(),
            status: this._status,
            companyRefCodes: this._companyRefCodes,
            localization: this._localization,
            createdDate: this._createdDate,
            updatedDate: this._updatedDate
        }

        if (this._accessLevel !== undefined) {
            catalog.accessLevel = this._accessLevel;
        }

        if (this._altImages !== undefined) {
            catalog.altImages = this._altImages.map((imageRef: ImageRef) => imageRef.serialize());
        }

        if (this._classification !== undefined) {
            catalog.classification = this._classification.serialize();
        }

        if (this._externalCodes !== undefined) {
            catalog.externalCodes = this._externalCodes;
        }

        if (this._accessStatus !== undefined) {
            catalog.accessStatus = this._accessStatus;
        }

        if (this._catalogVersions !== undefined) {
            catalog.catalogVersions = this._catalogVersions.map((catalogVersion: CatalogVersion) => catalogVersion.serialize());
        }

        if (this._pricing !== undefined) {
            catalog.pricing = this._pricing.serialize();
        }

        if (this._properties !== undefined) {
            catalog.properties = this._properties.serialize();
        }

        return catalog;
    }
}

export interface IContentAPICatalog extends IContentAPICatalogBaseInfo {
    status: CATALOG_STATUS;
    companyRefCodes?: Array<string>;
    localization: ILocalization;
    accessLevel?: string;
    createdDate: string;
    updatedDate: string;
    altImages?: Array<IContentAPIImageRef>;
    classification?: IContentAPICatalogClassification;
    externalCodes?: IExternalCodes;
    accessStatus?: string;
    catalogVersions?: Array<IContentAPICatalogVersion>;
    pricing?: IContentAPIPricingTaskList;
    properties?: IContentAPICatalogProperties;
}